import { createRouter, createWebHistory } from 'vue-router'
import { store }  from "@/liquido/services/store.js"
import api from "@/liquido/services/liquido-graphql-client.js"
import { isJwtExpired } from "@/shared/services/jwt-payload.js"
import config from "config"
import log from 'loglevel'
//import { route } from 'fontawesome'
if (import.meta.env.MODE === "development") log.enableAll()

const routes = [
	{
		path: "/",
		name: "index",
		// Will forward anonymous to /welcome 
		// With valid JWT to /team
		// and with expired JWT to /login
	},
	{
		path: "/login",
		name: "login",
		component: () => import("@/liquido/views/login-page.vue"),
		props: route => ({
			email: route.query.email,
			emailToken: route.query.emailToken
		}),
		meta: {
			public: true
		}
	},
	{
		path: "/welcome",
		name: "welcome",
		// /joinTeam was the old dedicated join page. Invite links sent out before it was replaced
		// still point there. An alias (not a redirect) keeps the ?inviteCode= query intact.
		alias: "/joinTeam",
		component: () => import("@/liquido/views/welcome-chat-v2.vue"),
		props: route => ({
			inviteCodeQueryParam: route.query.inviteCode
		}),
		meta: {
			public: true
		}
	},

	// ========= authenticated pages ============

	{
		path: "/team",
		name: "team",
		component: () => import("@/liquido/views/team-home.vue"),
		props: true,  // teamId, teamName, teamLogoUrl
	},
	{
		path: "/userhome",
		name: "userhome",
		component: () => import("@/liquido/views/user-home.vue"),
	},
	{
		path: "/polls",
		name: "polls",
		component: () => import("@/liquido/views/polls.vue"),
	},
	{
		// The all-in-one poll editor, create mode (no pollId prop).
		// This is the same component as the edit mode, but without a pollId prop it will create a new poll.
		// Previously there was a seperate /polls/create component to create a new poll, but now we use the same component for both create and edit.
		path: "/polls/new",
		name: "newPoll",
		component: () => import("@/liquido/views/poll-edit.vue"),
		props: route => ({ onboarding: route.query.onboarding === "1" }),
	},
	{
		path: "/polls/:pollId",
		name: "showPoll",
		component: () => import("@/liquido/views/poll-show.vue"),
		props: true,
	},
	{
		// The all-in-one poll editor, edit mode (existing poll, pollId prop present).
		path: "/polls/:pollId/edit",
		name: "editPoll",
		component: () => import("@/liquido/views/poll-edit.vue"),
		props: true,
	},
	{
		// Admin only: pick the end of the voting phase and start it. Replaced the start modal of poll-show.vue.
		path: "/polls/:pollId/start",
		name: "startPoll",
		component: () => import("@/liquido/views/poll-start.vue"),
		props: true,
	},
	{
		path: "/polls/:pollId/add",
		name: "addProposal",
		component: () => import("@/liquido/views/proposal-add.vue"),
		props: true,
	},
	{
		path: "/polls/:pollId/castVote",
		name: "castVote",
		component: () => import("@/liquido/views/cast-vote.vue"),
		props: true,
	},
	{
		path: "/polls/:pollId/winner",
		name: "pollWinner",
		component: () => import("@/liquido/views/poll-winner.vue"),
		props: true,
	},
	{	
		path: "/forgotPassword",
		name: "forgotPassword",
		component: () => import("@/liquido/views/forgot-password.vue"),
		meta: {
			public: true
		}
	},
	{
		path: "/login-via-sms",
		name: "loginSms",
		component: () => import("@/liquido/views/login-via-sms.vue"),
		meta: {
			public: true
		}
	},
	{	
		path: "/resetPassword",
		name: "resetPassword",
		component: () => import("@/liquido/views/forgot-password.vue"),
		// Optional query parameters: emailInputVal, resetPasswordToken
		meta: {
			public: true
		}
	},
	{
		// Public: the user clicks this straight out of their mail client, not logged in.
		path: "/verifyEmail",
		name: "verifyEmail",
		component: () => import("@/liquido/views/verify-email.vue"),
		props: route => ({ verifyToken: route.query.verifyToken }),
		meta: { public: true },
	},
	{
		path: "/impressum",
		name: "impressum",
		component: () => import("@/shared/views/impressum.vue"),
		meta: {
			public: true
		}
	},
	{
		path: "/agb",
		name: "agb",
		component: () => import("@/shared/views/agb.vue"),
		meta: {
			public: true
		}
	},
	{
		path: "/datenschutz",
		name: "datenschutz",
		component: () => import("@/shared/views/datenschutz.vue"),
		meta: {
			public: true
		}
	},
	{
		path: "/404",
		name: "pageNotFound",
		component: () => import("@/liquido/views/not-found-page.vue"),
		//props: true,  //MAYBE: where to go "back" ?
		meta: {
			public: true
		}
	},
	{
		path: "/:pathMatch(.*)*",
		redirect: "404",
	},
]

if (import.meta.env.MODE === "development") {
	routes.push({
		// Development login that can be used in testing
		path: "/devLogin",
		name: "devLogin",
		component: () => import("@/liquido/views/dev-login.vue"),
		props: route => ({
			email: route.query.email,
			teamName: route.query.teamName,
			// dev-login.vue declares this prop as `token`, and cy.devLogin() sends ?token=...
			// Mapping it to `emailToken` here meant the prop was never set and devLogin always
			// failed with "Need email and devLoginToken!".
			token: route.query.token
		}),
		meta: {
			public: true
		}
	})
	routes.push({
    path: '/_design-overview',
    component: () => import('@/liquido/views/_design-overview.vue'),
		meta: {
			public: true
		}
  })
}

const router = createRouter({
	// We use `createWebHistory()` for clean URL paths.
	// `createWebHashHistory()` with the hash(#) in the URL was necessary for iOS web app, but now also works fine like this.
	history: createWebHistory(config.BASE_URL),

	// Disable scroll behavior to prevent history.state warnings
	// Scroll position is managed in root-app.vue watch.$route instead
	scrollBehavior: () => false,
	
	routes,
})

const IS_ALREADY_AUTHENTICATED = -1
const IS_ANONYMOUS = -99
const SESSION_EXPIRED = -98

/** The login page, saying why the user is there: their login ran out (a token lasts 7 days, renewed while the app is used; a login 30 days at most) */
const LOGIN_AGAIN = { name: "login", query: { sessionExpired: "1" } }

/**
 * On every request we try to authenticate the current user.
 * IF the user is already successfully authenticated (JWT, team & user info is loaded, JWT not expired)
 * THEN return immideately
 * IF there is a stored JWT in localStorage that has expired, THEN log out: the backend would refuse it.
 * IF there is a stored JWT in localStorage,
 * THEN try to authenticate with it against the backend.
 * IF this fails, then remove the (expired or invalid) JWT from localstorage
 * ELSE return login information
 *
 * @return A Promise that will resolve to the login data or reject when no, invalid or expired JWT.
 */
async function tryToAuthenticate() {
	if (api.isAuthenticated()) return Promise.resolve(IS_ALREADY_AUTHENTICATED);  // false for an expired JWT
	let jwt = localStorage.getItem(api.LIQUIDO_JWT_KEY);
	//log.debug("tryToAuthenticate jwt: ", jwt)
	if (jwt && isJwtExpired(jwt)) {
		log.debug("JWT in localStorage has expired. Logging out.")
		api.logout()
		return Promise.reject(SESSION_EXPIRED)
	}
	if (jwt) {
		log.debug("Attempting to login with JWT from localStorage ...")
		return api.loginWithJwt(jwt)
			.then(res => {
				log.info("Successfully authenticated from localStorage")
				return Promise.resolve(res)
			})
			.catch(err => {
				// return liquido error code, eg. JWT_TOKEN_EXPIRED or JWT_TOKEN_INVALID
				let errCode = err?.liquidoException?.liquidoErrorCode ?? -1   // a GraphQL error: graphQlQuery put it there
				if (errCode === api.err.JWT_TOKEN_EXPIRED || errCode === api.err.JWT_TOKEN_INVALID) {
					localStorage.removeItem(api.LIQUIDO_JWT_KEY)
					log.debug("Removed expired/invalid JWT from localStorage")
				} else {
					log.debug("Cannot login with JWT. Will be removed.")  // err has already been logged
				}
				api.logout()
				return Promise.reject(err)
			})
	}
	else return Promise.reject(IS_ANONYMOUS) // no JWT, not authenticated at all
}


/**
 * Vue Router - Navigation guard
 * 
 * 1) Try to authenticate
 *
 * IF user is already authenticated THEN continue
 * ELSE IF there is a stored JWT in localStorage
 * THEN try to login with it at the backend.
 * 
 * IF user is now authenticated
 * 	IF an authenticated user navigates to '/'
 *  THEN forard him directly to his team-home page.
 *  ELSE let him continue
 * 
 * IF user is still not authenticated
 *   IF route is public then continue
 *   ELSE IF he navigates to "/" forward him to "/welcome"
 *   ELSE forward to "/login"
 * 
 * VUE Router next (for VUE 3)
 * https://next.router.vuejs.org/guide/advanced/navigation-guards.html#navigation-guards
 */
router.beforeEach(async (routeTo, routeFrom) => {
	//log.debug("beforeEach ENTER", routeFrom.path, "=>", routeTo.path)
	
	// Clear header title. Page may set it later when it is mounted.
	if (routeFrom.path !== routeTo.path) {
		//console.log("===== Router: Clear header")
		store.setHeaderTitle(undefined)
		store.setHeaderBackTarget(undefined)
		store.clearHeaderRight()
	}
	
	return tryToAuthenticate().then(() => {
		log.debug("vue-router: authenticated", routeFrom.path, routeFrom.params, "=>", routeTo.path, routeTo.params)
		// An authenticated visitor has no use for the registration landing page either - straight to
		// their team, same as "/".
		// Exception: _design-overview.vue embeds /welcome in iframes with ?forceAnonymous=1, so that it can
		// show the registration screens while the developer is logged in. Development mode only.
		const forceAnonymousWelcome = import.meta.env.MODE === "development" && routeTo.query.forceAnonymous === "1"
		// Exception 2: an invite link (/welcome?inviteCode=, or the old /joinTeam alias). A logged-in user may join
		// ANOTHER team with it (UC 2) - welcome-chat-v2.vue offers that. Redirecting it to /team lost the invite
		// from 2026-09-21 on, and no test noticed: every join test was anonymous.
		const inviteLink = routeTo.name === "welcome" && !!routeTo.query.inviteCode
		if (routeTo.path === "/" || routeTo.path === "/index.html" || (routeTo.name === "welcome" && !forceAnonymousWelcome && !inviteLink)) {
			return {name: "team"}
		} else {
			return true // allow authenticated navigation
		}
	}).catch(err => {
		log.debug("vue-router: anonymous", routeFrom.path, "=>", routeTo.path)
		if (routeTo.meta.public) {
			return true
		} else if (err === SESSION_EXPIRED) {
			return LOGIN_AGAIN   // a returning user, also on "/": not the registration page
		} else if (routeTo.path === "/" || routeTo.path === "/index.html") {
			return {name: "welcome"}
		}	else {
			return {name: "login"}
		}
	})
})

// The backend refused the session's JWT while the user was on a page - the client has logged them out already.
api.setSessionExpiredHandler(() => {
	if (router.currentRoute.value.name !== "login") router.push(LOGIN_AGAIN)
})

export default router

